import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { Bold, Heading2, Heading3, Pilcrow, List, Link2, Unlink, Undo2, Redo2 } from "lucide-react";

function Tool({ on, label, icon: Icon, onClick, testId }) {
  return (
    <button type="button" aria-label={label} title={label} data-testid={testId} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      className={`h-10 w-10 rounded-md flex items-center justify-center ${on ? "bg-amber-800 text-white" : "text-stone-700 hover:bg-stone-100"}`}>
      <Icon className="h-4 w-4" />
    </button>
  );
}

export function RichTextEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: { levels: [2, 3] }, codeBlock: false, code: false, blockquote: false, horizontalRule: false, strike: false }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer" } })],
    content: value,
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
    editorProps: { attributes: { "data-testid": "legal-editor-content", class: "min-h-[50vh] max-h-[65vh] overflow-y-auto p-4 outline-none text-[15px] leading-relaxed [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mt-5 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-amber-900 [&_h3]:mt-3 [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_a]:text-amber-800 [&_a]:underline" } },
  });
  if (!editor) return null;
  const c = () => editor.chain().focus(); // lazy: chain().focus() focuses immediately, so never call it during render
  const link = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link address (https://… or mailto:…)", prev);
    if (url === null) return;
    if (!url.trim()) c().unsetLink().run();
    else c().extendMarkRange("link").setLink({ href: url.trim() }).run();
  };
  return (
    <div data-testid="legal-editor" className="rounded-lg border border-stone-300 bg-white">
      <div className="flex flex-wrap gap-0.5 border-b border-stone-200 p-1 sticky top-0 bg-white z-10">
        <Tool label="Heading" icon={Heading2} testId="rte-h2" on={editor.isActive("heading", { level: 2 })} onClick={() => c().toggleHeading({ level: 2 }).run()} />
        <Tool label="Subheading" icon={Heading3} testId="rte-h3" on={editor.isActive("heading", { level: 3 })} onClick={() => c().toggleHeading({ level: 3 }).run()} />
        <Tool label="Paragraph" icon={Pilcrow} testId="rte-p" on={editor.isActive("paragraph")} onClick={() => c().setParagraph().run()} />
        <Tool label="Bold" icon={Bold} testId="rte-bold" on={editor.isActive("bold")} onClick={() => c().toggleBold().run()} />
        <Tool label="Bullet list" icon={List} testId="rte-bullets" on={editor.isActive("bulletList")} onClick={() => c().toggleBulletList().run()} />
        <Tool label="Add link" icon={Link2} testId="rte-link" on={editor.isActive("link")} onClick={link} />
        <Tool label="Remove link" icon={Unlink} testId="rte-unlink" onClick={() => c().unsetLink().run()} />
        <Tool label="Undo" icon={Undo2} testId="rte-undo" onClick={() => c().undo().run()} />
        <Tool label="Redo" icon={Redo2} testId="rte-redo" onClick={() => c().redo().run()} />
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
